// Angular
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
// Store
import { Store } from '@ngxs/store';
import { QualityState } from '../state/quality.state';
// Model
import { RuleEngine } from '../rules/model/rule-engine';
import { OrderDetailModel } from '../state/model/order-detail.model';
import { DOCUMENT_ATTACHMENT_CATEGORIES, DOCUMENT_RULES_UI_SECTIONS } from '../model/quality.constant';
import { RuleModel } from '../../../../../cv.rule-engine.ux/src/lib/common/models/rule-set.model';
// Component
import { NgxExtendedPdfViewerComponent } from 'ngx-extended-pdf-viewer';
// Service
import { QualityDocumentService } from '../service/document-quality.service';
import { NgxExtendedPdfViewerService } from 'ngx-extended-pdf-viewer';
import { BroadcastService } from '@cv-invision/core';
// Other
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';

declare const PDFViewerApplication: any;

@Component({
	selector: 'cv-pdf-review',
	templateUrl: './pdf-review.component.html',
	styleUrls: ['./pdf-review.component.scss']
})
export class PdfReviewComponent implements OnInit, OnDestroy {
	@ViewChild('ngxExtendedPdfViewerComponent', { static: false }) ngxExtendedPdfViewerComponent: NgxExtendedPdfViewerComponent | undefined;
	orderDetail$: Observable<OrderDetailModel> = this.store.select(QualityState.getOrderDetail);
	private atComponentDestroy = new Subject();
	src: any;
	public loading: boolean;
	public isAvailable: boolean;
	public ruleModel: RuleModel | null = null;
	public pdfPage = 1;
	public pageContainer: Element | null = null;
	public currentZoom = 0.75;
	private userManuallyChangedZoom = false;
	private pdfLoaded$ = new BehaviorSubject<boolean>(false);
	private ruleModel$ = new BehaviorSubject<RuleModel | null>(null);
	private hasEmitted = false;
	private lastLoadedPage$ = new Subject<number>();
	private maxPageLoaded = 0;

	constructor(
		private store: Store,
		public ngxExtendedPdfViewerService: NgxExtendedPdfViewerService,
		private qualityDocumentService: QualityDocumentService,
		private broadcastService: BroadcastService
	) {
		this.src = null;
		this.loading = true;
		this.isAvailable = true;
	}

	ngOnInit(): void {
		RuleEngine.setSelectedSection(this.store, DOCUMENT_RULES_UI_SECTIONS.PDFREVIEW);
		this.lastLoadedPage$
			.pipe(
				debounceTime(300),
				filter(() => !this.hasEmitted),
				takeUntil(this.atComponentDestroy)
			)
			.subscribe(finalPage => {
				this.hasEmitted = true;
				this.pdfLoaded$.next(true);
			});

		this.store
			.select(state => state.quality.rules.selectedRule)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(ruleModel => {
				this.ruleModel$.next(ruleModel);
			});

		combineLatest([this.pdfLoaded$, this.ruleModel$])
			.pipe(
				filter(([loaded, rule]) => loaded && !!rule),
				takeUntil(this.atComponentDestroy)
			)
			.subscribe(([_, rule]) => {
				this.ruleModel = rule!;
				this.highlightPDFElement(true);
			});

		this.orderDetail$.pipe(takeUntil(this.atComponentDestroy)).subscribe(orderDetailModel => {
			if (orderDetailModel?.id) {
				this.loadPDFDocument(orderDetailModel.id);
			}
		});
	}

	loadPDFDocument(orderId: number): void {
		if (this.qualityDocumentService.isLoadingPDF()) {
			this.broadcastService.on('loadPDFSuccess', () => {
				this.loading = false;
				if (this.qualityDocumentService.currentPDFFileId() === orderId) {
					this.src = this.qualityDocumentService.loadCurrentPDF();
				}
			});
			this.broadcastService.on('loadPDFFail', result => {
				if (result === 'Not Available') {
					this.isAvailable = false;
				}
			});
		} else {
			if (this.qualityDocumentService.currentPDFFileId() === orderId) {
				this.loading = false;
				this.src = this.qualityDocumentService.loadCurrentPDF();
			} else {
				this.downloadPDFDocument(orderId);
			}
		}
	}

	downloadPDFDocument(orderId: number): void {
		this.loading = true;
		this.qualityDocumentService
			.getOrderDocumentAttachment(orderId, DOCUMENT_ATTACHMENT_CATEGORIES.PDF)
			.subscribe(result => {
				if (result.length) {
					this.qualityDocumentService
						.downloadPDFFile(orderId, result[0].path)
						.subscribe(pdfResponse => {
							this.loading = false;
							this.src = pdfResponse.blob;
						});
				} else {
					this.isAvailable = false;
				}
			});
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();

		this.removeAllHighlights();
	}

	getPdfReviewHeight(): string {
		let pdfHieght = '83vh';

		if (window.innerHeight > 750 && window.innerHeight < 900) {
			pdfHieght = '80vh';
		} else if (window.innerHeight <= 749) {
			pdfHieght = '77vh';
		}

		return pdfHieght;
	}

	public handlePagesLoaded(event: any) {
		if (event.pagesCount > this.maxPageLoaded) {
			this.maxPageLoaded = event.pagesCount;
			this.lastLoadedPage$.next(this.maxPageLoaded);
		}

		console.log('PDF loaded successfully');
	}

	public handleZoomChange(event: any) {
		if (typeof event === 'number' && event > 0) {
			this.currentZoom = event;

			if (this.ruleModel?.variables?.ref_box) {
				this.userManuallyChangedZoom = true;
				this.highlightPDFElement();
			}
		}
	}

	public highlightPDFElement(scrollToHighlight: boolean = false): void {

		this.removeAllHighlights();

		if (
			this.ngxExtendedPdfViewerComponent &&
			this.ruleModel !== null &&
			this.ruleModel.uiSection === DOCUMENT_RULES_UI_SECTIONS.PDFREVIEW
		) {
			const pdfSearch = this.ruleModel.variables;
			if (pdfSearch && pdfSearch.ref_page && pdfSearch.ref_box) {
				// Set the PDF viewer to the target page
				this.pdfPage = pdfSearch.ref_page;

				// If the user has not manually changed zoom, apply automatic zoom to 150%
				if (!this.userManuallyChangedZoom) {
					PDFViewerApplication.pdfViewer.currentScale = 1.5;
					this.currentZoom = 1.5;
				}

				let retries = 10;

				// Main function that draws red-corner highlights on the specified page
				const tryDrawHighlight = () => {
					const pageView = PDFViewerApplication.pdfViewer.getPageView(pdfSearch.ref_page - 1);
					const pdfViewer = this.ngxExtendedPdfViewerComponent['elementRef'].nativeElement.querySelector('.pdfViewer') as HTMLElement;
					const pageContainer = pdfViewer.querySelector(`.page[data-page-number="${pdfSearch.ref_page}"]`) as HTMLElement;

					// If the page hasn't rendered yet, retry a few times
					if (!pageContainer || !pageView?.viewport) {
						if (retries-- > 0) {
							setTimeout(tryDrawHighlight, 200);
						}
						return;
					}

					this.pageContainer = pageContainer;

					// Remove any existing red-corner highlights to avoid duplicates
					const oldCorners = pageContainer.querySelectorAll('.highlight-corner');
					oldCorners.forEach(el => el.remove());

					// Parse bounding box coordinates from ref_box (PDF coordinates)
					const [left, top, width, height] = pdfSearch.ref_box.split('|').map(coord => parseFloat(coord));
					const box = [left, top, left + width, top + height];

					// Convert PDF coordinates to screen coordinates using the current viewport transform
					const viewport = pageView.viewport;
					const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(box);

					const scaledLeft = Math.min(x1, x2);
					const scaledTop = Math.min(y1, y2);
					const scaledWidth = Math.abs(x2 - x1);
					const scaledHeight = Math.abs(y2 - y1);

					// Style definitions for red corner markers
					const lineLength = 20;
					const lineThickness = 8;
					const color = 'red';

					const createCornerLine = (styles: any) => {
						const line = document.createElement('div');
						Object.assign(line.style, styles);
						line.classList.add('highlight-corner');
						return line;
					};

					// Draw 8 red corner lines to indicate selection on all four corners
					const corners = [
						createCornerLine({ position: 'absolute', backgroundColor: color, width: `${lineLength}px`, height: `${lineThickness}px`, left: `${scaledLeft}px`, top: `${scaledTop}px` }),
						createCornerLine({ position: 'absolute', backgroundColor: color, width: `${lineThickness}px`, height: `${lineLength}px`, left: `${scaledLeft}px`, top: `${scaledTop}px` }),
						createCornerLine({ position: 'absolute', backgroundColor: color, width: `${lineLength}px`, height: `${lineThickness}px`, left: `${scaledLeft + scaledWidth - lineLength}px`, top: `${scaledTop}px` }),
						createCornerLine({ position: 'absolute', backgroundColor: color, width: `${lineThickness}px`, height: `${lineLength}px`, left: `${scaledLeft + scaledWidth - lineThickness}px`, top: `${scaledTop}px` }),
						createCornerLine({ position: 'absolute', backgroundColor: color, width: `${lineLength}px`, height: `${lineThickness}px`, left: `${scaledLeft}px`, top: `${scaledTop + scaledHeight - lineThickness}px` }),
						createCornerLine({ position: 'absolute', backgroundColor: color, width: `${lineThickness}px`, height: `${lineLength}px`, left: `${scaledLeft}px`, top: `${scaledTop + scaledHeight - lineLength}px` }),
						createCornerLine({ position: 'absolute', backgroundColor: color, width: `${lineLength}px`, height: `${lineThickness}px`, left: `${scaledLeft + scaledWidth - lineLength}px`, top: `${scaledTop + scaledHeight - lineThickness}px` }),
						createCornerLine({ position: 'absolute', backgroundColor: color, width: `${lineThickness}px`, height: `${lineLength}px`, left: `${scaledLeft + scaledWidth - lineThickness}px`, top: `${scaledTop + scaledHeight - lineLength}px` })
					];

					corners.forEach(corner => pageContainer.appendChild(corner));

					// Reset zoom flag after drawing
					this.userManuallyChangedZoom = false;

					// Scroll to the target area only if explicitly requested (e.g., rule was clicked)
					requestAnimationFrame(() => {
						const viewerContainer = document.querySelector('#viewerContainer') as HTMLElement;
						if (scrollToHighlight && viewerContainer) {
							viewerContainer.scrollTo({
								top: pageContainer.offsetTop + scaledTop - 40,
								left: pageContainer.offsetLeft + scaledLeft - 40,
								behavior: 'auto'
							});
						}
					});
				};

				// Sets up an observer to re-trigger tryDrawHighlight() if the page re-enters the viewport
				const initPageRedrawWatcher = (refPage: number) => {
					const viewerContainer = document.querySelector('#viewerContainer');
					const pageEl = document.querySelector(`.page[data-page-number="${refPage}"]`);

					if (!viewerContainer || !pageEl) return;

					const observer = new IntersectionObserver((entries, obs) => {
						for (const entry of entries) {
							if (entry.isIntersecting) {
								// When the target page is back in view, redraw the highlight
								tryDrawHighlight();
								obs.unobserve(entry.target);
							}
						}
					}, {
						root: viewerContainer,
						threshold: 0
					});

					observer.observe(pageEl);
				};

				// Draw the highlight immediately after rule is selected
				tryDrawHighlight();

				// Register the observer to ensure highlight is redrawn if page is re-entered later
				initPageRedrawWatcher(pdfSearch.ref_page);
			} else if (pdfSearch && pdfSearch.ref_text) {
				if (pdfSearch.ref_page) {
					this.pdfPage = pdfSearch.ref_page;
				}

				PDFViewerApplication.pdfViewer.currentScale = 1.5;
				this.currentZoom = 1.5;

				this.ngxExtendedPdfViewerService.find(
					pdfSearch.ref_text,
					{ highlightAll: true, ignoreAccents: true });

			} else if (pdfSearch && pdfSearch.ref_page) {
				PDFViewerApplication.pdfViewer.currentScaleValue = '1.5';
				PDFViewerApplication.pdfViewer.scrollPageIntoView({ pageNumber: parseInt(pdfSearch.ref_page, 10) });
			}
		}
	}

	/**
	 * Removes all highlighted text from the PDF viewer.
	 * If the PDF viewer contains highlighted text, this method removes all of the highlights from the page.
	 * If the PDF viewer is using the ngx-extended-pdf-viewer component, the method also clears the last text search.
	 *
	 * @returns {void}
	 *    This method does not return a value.
	 */
	public removeAllHighlights(): void {
		if (this.pageContainer !== null) {
			const existingHighlights = this.pageContainer.querySelectorAll('.highlight-corner');
			existingHighlights.forEach(highlight => highlight.remove());
		}

		if (this.hasSearchHighlight()) {
			this.ngxExtendedPdfViewerService.find('');
		}
	}

	/**
	 * Checks if there are any active search highlights in the PDF viewer.
	 * This method queries the DOM for elements with the `.highlight` class
	 * inside `.textLayer`, which is how ngx-extended-pdf-viewer (and PDF.js)
	 * currently renders search highlights.
	 * 
	 * CAUTION:
	 * This logic depends on the internal DOM structure of ngx-extended-pdf-viewer,
	 * which may change in future versions. If the class name `.highlight` is renamed,
	 * or the rendering strategy changes (e.g., using Shadow DOM or different wrappers),
	 * this check may no longer work.
	 * Always verify this after upgrading the viewer library.
	 * @returns 
	 */
	public hasSearchHighlight(): boolean {
		const highlightElements = document.querySelectorAll('.textLayer .highlight');
		return highlightElements.length > 0;
	}
}
